import React, { useEffect, useRef, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { Key, Clock, Home, Brain, Music, Waves, Sparkles, Heart, ChevronDown, Check, X } from "lucide-react";
import { useToast } from "../hooks/use-toast";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ASSETS = {
  vslMain: "https://customer-assets.emergentagent.com/job_domain-landing-2/artifacts/zsw2yq9q_VSL%20LANGING%20ES.mov",
  vslProof: "https://base44.app/api/apps/6a0348cfc56547bfb397ec17/files/mp/public/6a0348cfc56547bfb397ec17/f53156c69_vsl_main.mp4",
  imgBase: "https://customer-assets.emergentagent.com/job_domain-landing-2/artifacts/u6ag0zk3_LA%20BASE.PNG",
  imgOnda: "https://customer-assets.emergentagent.com/job_domain-landing-2/artifacts/jvd45u3b_LA%20ONDA.PNG",
  imgEspacio: "https://customer-assets.emergentagent.com/job_domain-landing-2/artifacts/1rsmd7sy_EL%20ESPACIO.PNG",
  imgPain: "https://media.base44.com/images/public/6a0348cfc56547bfb397ec17/fec0e95dd_Screenshot2026-05-15at100937.png",
  imgYarima: "https://media.base44.com/images/public/6a0348cfc56547bfb397ec17/a16d13e63_Screenshot2026-05-15at101006.png",
  imgGuide: "https://media.base44.com/images/public/6a0348cfc56547bfb397ec17/5093c5f61_Screenshot2026-05-15at101032.png",
  proof1: "https://media.base44.com/images/public/6a0348cfc56547bfb397ec17/2ebd45e1b_WhatsAppImage2026-05-14at194833.jpg",
  proof2: "https://media.base44.com/images/public/6a0348cfc56547bfb397ec17/cd152b415_WhatsAppImage2026-05-14at1948332.jpg",
  proof3: "https://media.base44.com/images/public/6a0348cfc56547bfb397ec17/dd292c86f_WhatsAppImage2026-05-14at194834.jpeg",
};

const FAQS = [
  { q: "¿Necesito experiencia previa en el baile?", a: "Sí. Los 3 Fundamentos están diseñados para mujeres que ya tienen nociones de baile y quieren pasar del nivel técnico al artístico. Si sabes seguir un ritmo y conoces algunos pasos, estás lista." },
  { q: "¿Que hace diferente a este método?", a: "La mayoría de los métodos te enseñan a hacer más. Los 3 Fundamentos van más allá: no solo cambian cómo te mueves, sino también cómo tu cuerpo vive el baile. Combinan neurociencia, anatomía fascial y sabiduría corporal." },
  { q: "¿Cuánto tiempo le tengo que dedicar?", a: "Solo 10 minutos al día durante 21 días. Una práctica corta, constante y profunda que cambia cómo te sientes al bailar sin necesitar horas de ensayo." },
  { q: "¿Cómo funciona tu producto?", a: "Recibes una guía digital con los 3 Fundamentos (La Base, La Onda y El Espacio) y una autoevaluación para identificar tu bloqueo principal. Cada fundamento incluye una práctica de 10 minutos que puedes hacer desde casa, a tu ritmo y sin sentirte observada." },
  { q: "¿Sirve para otros bailes latinos?", a: "Los fundamentos nacen del Son Cubano pero trascienden cualquier estilo.\nLA BASE → Conexión con el suelo en salsa, bachata, kizomba\nLA ONDA → Fluidez en todas tus transiciones\nEL ESPACIO → Musicalidad y presencia universal\nEs como aprender anatomía del movimiento. Una vez integrado, TODO tu baile se eleva." },
  { q: "¿Necesito pareja de baile?", a: "No. Toda la práctica está diseñada para hacerla sola, en casa, sin necesidad de pareja ni espacio especial." },
  { q: "¿Es otro curso de pasos y coreografías?", a: "No. Aquí no aprendes pasos nuevos. Trabajas con lo que ya tienes para transformar cómo se ve y cómo se siente. Es una práctica de presencia, no de técnica." },
  { q: "¿Cómo accedo al producto?", a: "Deja tu nombre y tu email en el formulario de abajo. Te enviamos la guía al instante, de forma completamente gratuita." },
];

const FEATURES = [
  { icon: Key, title: "Los 3 Fundamentos", desc: "Convierten pasos correctos en presencia, confianza y magnetismo en 21 días." },
  { icon: Clock, title: "10 minutos al día", desc: "Una práctica corta y constante que cambia cómo te sientes al bailar." },
  { icon: Home, title: "A solas y sin juicio", desc: "Aprende desde casa, a tu ritmo, sin compararte con nadie." },
  { icon: Brain, title: "Diagnóstico claro", desc: "Entiende mejor tu bloqueo y da con la práctica que más te puede ayudar." },
  { icon: Music, title: "Sin coreografías nuevas", desc: "Trabajas con lo que ya sabes para que se vea y se sienta diferente." },
  { icon: Waves, title: "Reconexión del cuerpo", desc: "Te ayuda a soltar tensión y a sentirte más segura sin fingir ni forzar." },
  { icon: Sparkles, title: "Claves reales de escenario profesional", desc: "Lleva a tu baile social lo que hace magnética a una artista con 15 años de experiencia." },
  { icon: Heart, title: "Un método que no te empuja", desc: "Tu cuerpo no necesita más presión. Necesita confianza, seguridad y espacio para abrirse." },
];

const PAIN_ITEMS = [
  "Te sabes los pasos. Sabes seguir, sabes verte bien, sabes ejecutar.",
  "Pero aun así, tu cuerpo no se siente realmente libre.",
  "Y en el momento en que te miran, algo se tensa.",
  "Más técnica te da más control, pero no más libertad.",
  "Más correcciones hacen que te pienses demasiado, no que te expreses mejor.",
  "Intentarlo más solo hace que tu cuerpo se ponga más rígido, no más suelto.",
  "Y tanta presión termina apagando justo eso que te hizo empezar: el disfrute.",
];

const COMPARE_ROWS = [
  { f: "Trabaja la presencia, no solo los pasos", v: [true, false, false, false, false] },
  { f: "Solo 10 minutos al día", v: [true, false, true, false, false] },
  { f: "Sin coreografías nuevas", v: [true, false, false, false, true] },
  { f: "Desbloquea tensión corporal profunda", v: [true, false, false, false, false] },
  { f: "Basado en neurociencia + sabiduría cubana", v: [true, false, false, false, false] },
];

function useFadeIn() {
  useEffect(() => {
    const els = document.querySelectorAll(".fade");
    const io = new IntersectionObserver((entries) => {
      entries.forEach((e) => {
        if (e.isIntersecting) {
          e.target.classList.add("visible");
          io.unobserve(e.target);
        }
      });
    }, { threshold: 0.12 });
    els.forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, []);
}

export default function LandingPage() {
  useFadeIn();
  const navigate = useNavigate();
  const [openFaq, setOpenFaq] = useState(null);
  const [form, setForm] = useState({ name: "", email: "", consent: false });
  const [submitting, setSubmitting] = useState(false);
  const formRef = useRef(null);
  const { toast } = useToast();

  const scrollToForm = (e) => {
    e?.preventDefault?.();
    formRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim()) return;
    setSubmitting(true);
    try {
      await axios.post(`${API}/leads`, form);
      const submittedName = form.name.trim().split(" ")[0];
      setForm({ name: "", email: "", consent: false });
      navigate("/gracias", { state: { name: submittedName } });
    } catch (err) {
      toast({ title: "Algo salió mal", description: "Inténtalo de nuevo en un momento.", variant: "destructive" });
    } finally {
      setSubmitting(false);
    }
  };

  const CTA = ({ children, wide, full }) => (
    <a href="#guia" onClick={scrollToForm} className={`btn ${wide ? "wide" : ""} ${full ? "full" : ""}`}>{children}</a>
  );

  return (
    <>
      {/* HERO */}
      <section className="hero">
        <div className="inner center">
          <span className="eyebrow">Sistema Corporal al Bailar · 3 Pilares · 21 Días</span>
          <h1 className="hero-h1">
            Descubre el sistema corporal de 3 fundamentos que transforma bailarinas técnicas en mujeres <em>magnéticas</em> en solo 21 días
          </h1>
          <p className="hero-sub">
            Deja de sentirte rígida, insegura o desconectada cada vez que bailas. Estos tres fundamentos te ayudarán a soltar la tensión que está frenando tu Expresión Corporal femenina, para que vuelvas a sentirte viva, con confianza y conectada con tu cuerpo cada vez que empieza la música.
          </p>
          <div className="vsl-wrap">
            <video controls playsInline preload="metadata">
              <source src={ASSETS.vslMain} type="video/quicktime" />
              <source src={ASSETS.vslMain} type="video/mp4" />
            </video>
          </div>
          <CTA wide>Descarga la guía gratuita y empieza a soltar tu seguridad, tu sensualidad y tu magnetismo natural.</CTA>
          <p className="hero-fud">100% gratuito · Sin tarjeta · Acceso inmediato</p>
        </div>
      </section>

      {/* Proof bar */}
      <div className="proof-bar">
        <strong>Mujeres como tú. Cambios que se sienten de verdad.</strong> &nbsp;·&nbsp; 222+ reacciones por publicación &nbsp;·&nbsp; 15 años de escenarios internacionales &nbsp;·&nbsp; "Algo se movilizó" — @Swan.yoga_oficial
      </div>

      {/* PAIN POINT */}
      <section className="sec fade">
        <div className="inner wide">
          <div className="two-col">
            <div>
              <span className="eyebrow" style={{ textAlign: "left" }}>El Problema Real</span>
              <h2 className="sec-ttl">Si bailar todavía se siente pesado, el problema no es que no te esfuerces lo suficiente…</h2>
              <p className="sec-body" style={{ marginBottom: 14, marginTop: 14 }}>
                <strong style={{ color: "var(--white)" }}>Es que te falta la base correcta.</strong>
              </p>
              <ul className="pain-list">
                {PAIN_ITEMS.map((t, i) => (
                  <li key={i}><span className="dash">—</span>{t}</li>
                ))}
              </ul>
              <p className="sec-body" style={{ marginBottom: 24 }}>
                No necesitas clases más largas, exigirte más ni perseguir la perfección. Lo que necesitas es una práctica diaria que te ayude a volver al cuerpo, soltar la tensión y recuperar la sensación de libertad al bailar. <strong style={{ color: "var(--gold-lt)" }}>Y para eso fueron creados estos 3 pilares fundamentales.</strong>
              </p>
              <CTA>→ Descarga la Guía Gratuita</CTA>
            </div>
            <div className="media-box pain-img" style={{ minHeight: 400, position: "relative" }}>
              <img src={ASSETS.imgPain} alt="Bailarina cubana" style={{ width: "100%", display: "block", objectFit: "cover", minHeight: 460, verticalAlign: "bottom" }} />
              <div style={{ position: "absolute", bottom: 0, left: 0, right: 0, padding: "22px 26px", background: "linear-gradient(to top, rgba(20,8,2,0.95), transparent)" }}>
                <p style={{ fontFamily: "'Playfair Display',serif", fontStyle: "italic", color: "var(--white)", fontSize: 15.5, lineHeight: 1.6, margin: 0 }}>
                  "No se trata de convertirte en otra persona.<br />Se trata de sacar a la luz esa versión de ti que ya estaba ahí."
                </p>
                <p style={{ color: "var(--gold)", fontSize: 12, fontWeight: 700, marginTop: 8, letterSpacing: "0.06em" }}>— YARIMA RODRIGUEZ</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* SOCIAL PROOF #1 */}
      <section className="sec s2 fade">
        <div className="inner center">
          <span className="eyebrow">Prueba Social</span>
          <h2 className="sec-ttl">Mujeres como tú. Cambios que <em>se sienten de verdad.</em></h2>
          <div className="gold-line"></div>
          <div style={{ maxWidth: 720, margin: "0 auto 18px", borderRadius: 12, overflow: "hidden", border: "1px solid var(--line)", boxShadow: "0 16px 56px rgba(0,0,0,0.5)" }}>
            <video controls playsInline preload="metadata" style={{ width: "100%", display: "block", background: "#000" }}>
              <source src={ASSETS.vslProof} type="video/mp4" />
            </video>
          </div>
          <p style={{ maxWidth: 640, margin: "0 auto 36px", color: "var(--text)", fontSize: 14.5, lineHeight: 1.65, fontStyle: "italic" }}>
            <span style={{ color: "var(--gold-lt)" }}>"Havana Rakatan"</span>, performed by the Cuban troupe Ballet Rakatan, at City Center's Latin dance festival. New York 2015.
          </p>
          <div style={{ marginTop: 18 }}>
            <CTA>→ Descarga la guía gratuita y descubre los consejos de una bailarina profesional con años de experiencia.</CTA>
          </div>
        </div>
      </section>

      {/* SECTION DIVIDER - CONOCE LOS 3 PILARES */}
      <section className="pillars-intro fade">
        <div className="inner center">
          <span className="eyebrow" style={{ color: "var(--gold-lt)" }}>Conoce</span>
          <h2 className="sec-ttl"><em>Los 3 Pilares</em></h2>
          <div className="gold-line"></div>
          <p className="sec-body" style={{ maxWidth: 620, margin: "0 auto 10px" }}>La Base · La Onda · El Espacio — los tres fundamentos que transforman tu manera de habitar tu cuerpo al bailar.</p>
          <p className="cursive" style={{ maxWidth: 620, margin: "12px auto 0", fontSize: 17 }}>El fuego como hilo conductor de transmutación.</p>
        </div>
      </section>

      {/* WIIFM #1 La Base */}
      <section className="sec s3 fade">
        <div className="inner wide">
          <div className="wiifm">
            <div className="media-box symbol-box">
              <img src={ASSETS.imgBase} alt="La Base · Símbolo" className="pilar-symbol" />
            </div>
            <div>
              <div className="pilar-head">
                <h3 className="pilar-name">La Base</h3>
                <p className="pilar-element">Tierra · Fuego contenido</p>
                <p className="pilar-keys">Potencia · Centro · Arraigo</p>
              </div>
              <h2 className="wiifm-ttl">Convierte pasos correctos en presencia, confianza y <em>magnetismo en 21 días.</em></h2>
              <p className="wiifm-body"><strong style={{ color: "var(--white)" }}>La Base</strong> te ayuda a sentirte más segura, estable y conectada con tu cuerpo. No desde el control, sino desde la presencia. Con solo 10 minutos al día, una práctica corta y constante cambia cómo te sientes al bailar — para siempre.</p>
              <CTA>→ Descarga la Guía con Los 3 Fundamentos</CTA>
            </div>
          </div>
        </div>
      </section>

      {/* WIIFM #2 La Onda */}
      <section className="sec fade">
        <div className="inner wide">
          <div className="wiifm flip">
            <div>
              <div className="pilar-head">
                <h3 className="pilar-name">La Onda</h3>
                <p className="pilar-element">Agua · Fuego en movimiento</p>
                <p className="pilar-keys">Fluidez · Ritmo · Magnetismo</p>
              </div>
              <h2 className="wiifm-ttl">Aprende desde casa, a tu ritmo, sin compararte con nadie — y <em>sin sentirte observada.</em></h2>
              <p className="wiifm-body"><strong style={{ color: "var(--white)" }}>La Onda</strong> desbloquea una sensualidad natural, sin forzar ni exagerar, te ayuda a reconectar con tu cuerpo y a sentirte más segura sin fingir ni forzar. Porque tu magnetismo no necesita ser aprendido — solo necesita ser recordado y liberado.</p>
              <CTA>→ Quiero Soltar Mi Onda Natural</CTA>
            </div>
            <div className="media-box symbol-box">
              <img src={ASSETS.imgOnda} alt="La Onda · Símbolo" className="pilar-symbol" />
            </div>
          </div>
        </div>
      </section>

      {/* WIIFM #3 El Espacio */}
      <section className="sec s3 fade">
        <div className="inner wide">
          <div className="wiifm">
            <div className="media-box symbol-box">
              <img src={ASSETS.imgEspacio} alt="El Espacio · Símbolo" className="pilar-symbol" />
            </div>
            <div>
              <div className="pilar-head">
                <h3 className="pilar-name">El Espacio</h3>
                <p className="pilar-element">Éter · Fuego irradiado</p>
                <p className="pilar-keys">Presencia · Expansión · Magia</p>
              </div>
              <h2 className="wiifm-ttl">Lleva a tu baile social lo que hace magnética a una artista con <em>15 años de experiencia.</em></h2>
              <p className="wiifm-body"><strong style={{ color: "var(--white)" }}>El Espacio</strong> te ayuda a ocupar tu lugar con elegancia, presencia y magnetismo. Hay algo que desarrollas con la experiencia que no se puede copiar viendo tutoriales: esa presencia que atrapa, esa seguridad que se siente real. Este fundamento, te lo lleva a ti.</p>
              <CTA>→ Quiero Mi Presencia Magnética</CTA>
            </div>
          </div>
        </div>
      </section>

      {/* SOCIAL PROOF #2 */}
      <section className="sec s2 fade">
        <div className="inner center">
          <span className="eyebrow">Más Prueba Social</span>
          <h2 className="sec-ttl">Lo que dicen quienes ya <em>lo están viviendo</em></h2>
          <div className="gold-line"></div>
          <div className="proof-grid two-cols">
            <ProofCard img={ASSETS.proof2} label={<><strong>YouTube · @Swan.yoga_oficial</strong> — "Gracias por esta maravilla… algo se movilizó"</>} />
            <ProofCard img={ASSETS.proof3} label={<><strong>YouTube · @ricsass2757</strong> — "Útil más allá del género · conciencia corporal"</>} />
          </div>
          <div style={{ marginTop: 36 }}>
            <CTA>→ Descarga la Guía Gratuita Ahora</CTA>
          </div>
        </div>
      </section>

      {/* FEATURES */}
      <section className="sec fade">
        <div className="inner center">
          <span className="eyebrow">Por Qué Funciona</span>
          <h2 className="sec-ttl">8 cosas que hacen únicos a <em>Los 3 Fundamentos</em></h2>
          <div className="gold-line"></div>
          <div className="feat-grid">
            {FEATURES.map((f, i) => {
              const Icon = f.icon;
              return (
                <div key={i} className="feat-card">
                  <div className="feat-icon"><Icon size={22} strokeWidth={1.8} /></div>
                  <h3>{f.title}</h3>
                  <p>{f.desc}</p>
                </div>
              );
            })}
          </div>
          <div style={{ marginTop: 40 }}>
            <CTA>→ Descarga la Guía Gratuita y obtén los 3 fundamentos que cambiarán completamente tu forma de bailar</CTA>
          </div>
        </div>
      </section>

      {/* ABOUT + COMPARE */}
      <section className="sec s2 fade">
        <div className="inner wide">
          <div className="two-col" style={{ marginBottom: 72 }}>
            <div className="about-text">
              <span className="eyebrow" style={{ textAlign: "left" }}>Sobre el Creador</span>
              <h2 className="sec-ttl" style={{ textAlign: "left", marginTop: 8 }}>Yarima Rodriguez</h2>
              <div className="gold-line left"></div>
              <p>Bailarina Profesional de Espectáculos Musicales, Profesora de Kundalini Yoga &amp; Thai Massage Practitioner.</p>
              <p className="cursive">15+ años llevando el Lady Styling por el Mundo, en Grandes Escenarios Internacionales.</p>
              <p>Acompaño a mujeres que <strong>ya dominan los pasos</strong> a convertirse en las <strong>Artistas Magnéticas</strong> que sus cuerpos saben Ser.</p>
              <p><strong>Combino Neurociencia, Anatomía Facial y Sabiduría Corporal para facilitar tu transformación Artística.</strong></p>
              <div style={{ marginTop: 24 }}>
                <CTA>→ Aprende con Yarima — Es Gratis</CTA>
              </div>
            </div>
            <div className="media-box" style={{ minHeight: 380 }}>
              <img src={ASSETS.imgYarima} alt="Yarima Rodriguez" style={{ width: "100%", display: "block", objectFit: "cover", minHeight: 400 }} />
            </div>
          </div>

          <div className="center">
            <span className="eyebrow">Aprende los secretos de presencia escénica de una artista cubana con 15 años de experiencia y siéntete magnética también en los socials</span>
            <h2 className="sec-ttl">El único método que trabaja desde dentro, <em>no desde la corrección</em></h2>
            <div className="gold-line"></div>
            <div style={{ marginBottom: 36 }}>
              <CTA>→ Descarga la Guía Gratis</CTA>
            </div>
          </div>
          <div className="compare-wrap">
            <table className="compare-table">
              <thead>
                <tr>
                  <th className="feat"></th>
                  <th className="brand">Los 3 Fundamentos<br />(Yarima)</th>
                  <th>Clases técnicas</th>
                  <th>Tutoriales online</th>
                  <th>Cursos de pasos</th>
                  <th>Practicar sola</th>
                </tr>
              </thead>
              <tbody>
                {COMPARE_ROWS.map((r, i) => (
                  <tr key={i}>
                    <td className="feat">{r.f}</td>
                    {r.v.map((v, j) => (
                      <td key={j}>{v ? <Check className="tick" size={20} strokeWidth={3} style={{ color: "#6fcf97", display: "inline" }} /> : <X size={20} strokeWidth={2.5} style={{ color: "rgba(255,255,255,0.25)", display: "inline" }} />}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* STEPS */}
      <section className="sec s3 fade">
        <div className="inner center">
          <span className="eyebrow">Tu Camino de 21 Días</span>
          <h2 className="sec-ttl">Tu camino de 21 días <em>empieza aquí</em></h2>
          <div className="gold-line"></div>
          <p className="sec-body" style={{ maxWidth: 520, margin: "0 auto" }}>Diseñado para ser inmediato, effortless y completamente desde casa.</p>
          <div className="steps3">
            <div className="step-card">
              <div className="step-num">1</div>
              <h3>Descarga la guía gratis</h3>
              <p>Recibe ahora la base de los 3 Fundamentos y una autoevaluación personalizada para descubrir qué necesita tu cuerpo para desbloquearse y sentirse más libre al bailar.</p>
            </div>
            <div className="step-card">
              <div className="step-num">2</div>
              <h3>Haz tu práctica diaria de 10 minutos</h3>
              <p>Avanza paso a paso con La Base, La Onda y El Espacio desde tu casa, en intimidad, a tu ritmo y sin sentirte observada.</p>
            </div>
            <div className="step-card">
              <div className="step-num">3</div>
              <h3>Empieza a notar el cambio</h3>
              <p>Verás cómo tu cuerpo pasa de la rigidez y el control a una presencia mucho más libre, confiada, sensual y viva, tanto en la pista como fuera de ella.</p>
            </div>
          </div>
          <div style={{ marginTop: 40 }}>
            <CTA>→ Obtén Tu Guía Ahora</CTA>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="sec fade">
        <div className="inner narrow">
          <div className="center">
            <span className="eyebrow">Preguntas Frecuentes</span>
            <h2 className="sec-ttl">Todo lo que necesitas saber</h2>
            <div className="gold-line"></div>
          </div>
          <div className="faq-list">
            {FAQS.map((f, i) => (
              <div key={i} className={`faq-item ${openFaq === i ? "open" : ""}`}>
                <div className="faq-q" onClick={() => setOpenFaq(openFaq === i ? null : i)}>
                  <span>{f.q}</span>
                  <ChevronDown size={20} className="faq-arr" />
                </div>
                <div className="faq-a">{f.a.split("\n").map((line, idx) => (
                  <span key={idx} style={{ display: "block" }}>{line}</span>
                ))}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* URGENCY + FORM */}
      <section className="urgency fade" id="guia" ref={formRef}>
        <h2>Tu cuerpo ya sabe cómo sentirse libre. <em>Solo necesita el sistema correcto.</em></h2>
        <p>Miles de bailarinas ya están viviendo este cambio. La guía es gratuita, el acceso es inmediato y el único paso es tomar la decisión.</p>

        <div className="form-shell">
          <div className="guide-art">
            <img src={ASSETS.imgGuide} alt="Guía 3 Fundamentos · Triángulo Sagrado del Lady Styling" />
            <div className="guide-cap">
              <span className="eyebrow" style={{ margin: 0 }}>Guía Gratuita · PDF</span>
              <p>Triángulo Sagrado del Lady Styling</p>
            </div>
          </div>
          <div className="form-box">
            <h3>Recibe los 3 Fundamentos Ahora</h3>
            <p>Acceso inmediato. Completamente gratis.</p>
            <form onSubmit={onSubmit}>
              <input className="fi" type="text" placeholder="Tu nombre" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
              <input className="fi" type="email" placeholder="Tu mejor email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
              <label className="consent-row">
                <input type="checkbox" required checked={form.consent} onChange={(e) => setForm({ ...form, consent: e.target.checked })} />
                <span>
                  Acepto recibir la guía y los correos formativos. He leído la{" "}
                  <Link to="/politica-privacidad" target="_blank">Política de Privacidad</Link> y los{" "}
                  <Link to="/terminos-condiciones" target="_blank">Términos y Condiciones</Link>.
                </span>
              </label>
              <button type="submit" className="btn full" disabled={submitting}>
                {submitting ? "Enviando..." : "→ Descarga la Guía Gratuita y conoce el Triángulo Sagrado del Lady Styling"}
              </button>
            </form>
            <p className="form-note">Tu información está segura. Sin spam. Puedes darte de baja en cualquier momento.</p>
          </div>
        </div>

        <p style={{ marginTop: 20, fontSize: 12, color: "rgba(176,144,112,0.5)" }}>100% gratuito · Sin tarjeta · Sin compromisos · Acceso inmediato</p>
      </section>

      <footer className="footer">
        <div className="footer-inner">
          <div className="footer-brand">
            <div className="footer-name">Yarima Rodriguez</div>
            <div className="footer-tag">Lady Styling · Sistema Corporal</div>
          </div>
          <nav className="footer-nav">
            <Link to="/politica-privacidad">Política de Privacidad</Link>
            <span className="footer-sep">·</span>
            <Link to="/terminos-condiciones">Términos y Condiciones</Link>
            <span className="footer-sep">·</span>
            <a href="mailto:contacto@yarimarodriguez.com">contacto@yarimarodriguez.com</a>
          </nav>
          <div className="footer-copy">© {new Date().getFullYear()} Yarima Rodriguez · Todos los derechos reservados</div>
        </div>
      </footer>
    </>
  );
}

function ProofCard({ img, label }) {
  return (
    <div className="proof-card">
      <img src={img} alt="" loading="lazy" />
      <div className="proof-lbl">{label}</div>
    </div>
  );
}
